import React, { useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import UserSelectors from 'Stores/User/Selectors'
import AuthSelectors from 'Stores/Auth/Selectors'
import UserActions from 'Stores/User/Actions'
import { Colors } from 'Theme'
import {
  IFButton,
  IFDialog,
  IFModal,
  IFText,
  IFTable,
  IFSkeleton,
} from 'Components'
import Styles from './UserChargingTokenList.module.css'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import InfinityEnums from 'Enums/InfinityEnums'
import AddCardForm from 'Containers/UserPage/AddCardForm/AddCardForm'
import RequestState from 'Enums/RequestState'
import { formatDate } from '../../Utils/StringFunctions'
import { Box, Popover } from '@mui/material'
import { Actions, Subjects } from 'Enums/permissionKeys'

const UserChargingTokenList = ({
  userId,
  currentUserChargingTokens,
  setCurrentUserChargingTokenActive,
  editingAdminRole,
  userDetailsRequestState,
  addCard,
  fetchUserListRequestState,
  addCurrentCardRequestState,
  selectedUserIndex,
}) => {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState(null)
  const [popoverContent, setPopoverContent] = useState('')
  const [error, setError] = useState('')
  const showDialogHandlerAddCard = useRef(null)
  const [selectedRow, setSelectedRow] = useState({})
  const canEditUser = editingAdminRole.can(Actions.EDIT, Subjects.USERS)

  const showDialogHandler = useRef()
  useEffect(() => {
    if (addCurrentCardRequestState === RequestState.SUCCEEDED) {
      showDialogHandlerAddCard?.current?.dismiss()
      setSelectedRow({})
    }
  }, [addCurrentCardRequestState])

  const handlePopoverOpen = (event, row) => {
    const { huid, contractId } = row.original
    const textOverFlow = event.currentTarget.querySelector(
      `.${Styles.TableText}`,
    )
    const isOverflowing = (element) =>
      element && element.scrollWidth > element.clientWidth

    if (isOverflowing(textOverFlow)) {
      setAnchorEl(event.currentTarget)
      setPopoverContent({ huid, contractId })
    }
  }

  const handlePopoverClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const chargingTokenTableColumns = useMemo(
    () => [
      {
        accessorKey: 'type',
        header:
          fetchUserListRequestState === RequestState.LOADING ||
          userDetailsRequestState === RequestState.LOADING ||
          selectedUserIndex === -1 ? (
            <IFSkeleton variant="text" width="110px" height={20} />
          ) : (
            t('UserChargingTokenList.type')
          ),
        Cell: ({ row }) => <IFText>{row.original.type}</IFText>,
      },
      {
        accessorKey: 'id',
        header:
          fetchUserListRequestState === RequestState.LOADING ||
          userDetailsRequestState === RequestState.LOADING ||
          selectedUserIndex === -1 ? (
            <IFSkeleton variant="text" width="110px" height={20} />
          ) : (
            t('UserChargingTokenList.id')
          ),
        Cell: ({ row }) => {
          return (
            <div
              className={Styles.ColumnTitle}
              onMouseEnter={(e) => handlePopoverOpen(e, row)}
              onMouseLeave={handlePopoverClose}
            >
              <IFText className={Styles.TableText}>{row.original.huid}</IFText>
            </div>
          )
        },
      },
      {
        accessorKey: 'contractId',
        header:
          fetchUserListRequestState === RequestState.LOADING ||
          userDetailsRequestState === RequestState.LOADING ||
          selectedUserIndex === -1 ? (
            <IFSkeleton variant="text" width="110px" height={20} />
          ) : (
            t('UserChargingTokenList.contractId')
          ),
        Cell: ({ row }) => {
          return (
            <div
              onMouseEnter={(e) => handlePopoverOpen(e, row)}
              onMouseLeave={handlePopoverClose}
            >
              <IFText className={Styles.TableText}>{`${
                row.original.contractId ? row.original.contractId : '-'
              }`}</IFText>
            </div>
          )
        },
      },
      {
        accessorKey: 'updatedAt',
        header:
          fetchUserListRequestState === RequestState.LOADING ||
          userDetailsRequestState === RequestState.LOADING ||
          selectedUserIndex === -1 ? (
            <IFSkeleton variant="text" width="110px" height={20} />
          ) : (
            t('UserChargingTokenList.updatedAt')
          ),
        Cell: ({ row }) => (
          <IFText>{formatDate(row.original.updatedAt)}</IFText>
        ),
      },

      ...(canEditUser
        ? [
            {
              accessorKey: 'deactivateButton',
              header:
                userDetailsRequestState === RequestState.UNINITIALIZED ||
                userDetailsRequestState === RequestState.LOADING ? (
                  <IFSkeleton variant="text" width="110px" height={20} />
                ) : (
                  t('UserChargingTokenList.deactivateButton')
                ),
              Cell: ({ row }) => (
                <div>
                  <div className={Styles.deactivateWrapper}>
                    <div className={Styles.deactivateContainer}>
                      {!canEditUser &&
                      row.original.status ===
                        InfinityEnums.ChargingTokenStatus.ACTIVE ? null : (
                        <IFButton
                          size="sm"
                          text={
                            !canEditUser
                              ? row.original.status ===
                                InfinityEnums.ChargingTokenStatus.DEACTIVATED
                                ? t('UserPage.Deactivated')
                                : t('UserChargingTokenList.Active')
                              : row.original.status ===
                                  InfinityEnums.ChargingTokenStatus.DEACTIVATED
                                ? t('UserPage.Deactivated')
                                : t('UserPage.DeactivateButton')
                          }
                          isDead={
                            row.original.status ===
                              InfinityEnums.ChargingTokenStatus.DEACTIVATED ||
                            !canEditUser
                          }
                          isLoading={row.original.isLoading}
                          color={Colors.UserPageDeactivateButton}
                          onClick={() => {
                            setSelectedRow(row)
                            showDialogHandler.current.show()
                          }}
                        />
                      )}
                    </div>
                    <IFDialog
                      ref={showDialogHandler}
                      open={false}
                      maxWidth={'xs'}
                      fullWidth={true}
                      title={t('UserPage.DeactivateTitleToken')}
                      bodyText={t('UserPage.DeactivateTokenText')}
                      buttonAcceptonClick={() => {
                        setCurrentUserChargingTokenActive(
                          userId,
                          selectedRow.original._id,
                          selectedRow.index,
                        )
                      }}
                      buttonCancelColor={Colors.UserPageCancel}
                      buttonAcceptColor={Colors.UserPageDeactivateButton}
                      buttonAcceptText={t('UserPage.DeactivateConfirm')}
                      buttonClassName={Styles.Button}
                    />
                  </div>
                </div>
              ),
            },
          ]
        : []),
    ],
    [
      currentUserChargingTokens,
      fetchUserListRequestState,
      userDetailsRequestState,
      selectedRow,
    ],
  )
  const renderToolbar = ({ table }) => {
    const isFullScreen = table.getState().isFullScreen
    return (
      <>
        {isFullScreen ? (
          <div className={Styles.TopToolBarFullScreen}>
            <IFText
              className={Styles.HeaderText}
              style={{ color: Colors.UserPageTextColor }}
              skeletonWidth="180px"
              loadSkeleton={
                fetchUserListRequestState === RequestState.LOADING ||
                userDetailsRequestState === RequestState.LOADING ||
                selectedUserIndex === -1
              }
            >
              {t('UserPage.ChargingTokensTitle')}
            </IFText>
            {canEditUser &&
              (fetchUserListRequestState === RequestState.LOADING ||
              userDetailsRequestState === RequestState.LOADING ||
              selectedUserIndex === -1 ? (
                <></>
              ) : (
                <div className={Styles.IconHover}>
                  <IFButton
                    size="sm"
                    color={Colors.primary}
                    text={t('UserPage.ChargingTokensButton')}
                    onClick={() => {
                      setError('')
                      showDialogHandlerAddCard.current.show()
                    }}
                  />
                </div>
              ))}
          </div>
        ) : (
          <div className={Styles.TopToolBar}>
            {canEditUser &&
              (fetchUserListRequestState === RequestState.LOADING ||
              userDetailsRequestState === RequestState.LOADING ||
              selectedUserIndex === -1 ? (
                <></>
              ) : (
                <div className={Styles.IconHover}>
                  <IFButton
                    size="sm"
                    color={Colors.primary}
                    text={t('UserPage.ChargingTokensButton')}
                    onClick={() => {
                      setError('')
                      showDialogHandlerAddCard.current.show()
                    }}
                  />
                </div>
              ))}
          </div>
        )}
      </>
    )
  }

  return (
    <>
      <IFTable
        columns={chargingTokenTableColumns}
        data={currentUserChargingTokens}
        showSkeleton={
          fetchUserListRequestState === RequestState.LOADING ||
          userDetailsRequestState === RequestState.LOADING ||
          selectedUserIndex === -1
            ? true
            : false
        }
        tableRequestState={userDetailsRequestState}
        tableMaxHeight={226}
        enableFullScreen={true}
        enableToolBar={true}
        renderToolbar={renderToolbar}
      />
      <IFModal
        title={t('UserPage.AddCardTitle')}
        open={false}
        fullWidth={true}
        ref={showDialogHandlerAddCard}
        className={Styles.AddCardForm}
      >
        <AddCardForm
          error={error}
          onSubmit={(value) => {
            const cleanedValue = value.replace(/(\r\n|\n|\r)/gm, ' ')

            const words = cleanedValue.split(' ')

            const formattedValue = words
              .map((word, index) => {
                if (index === words.length - 1) {
                  return word
                }
                return word.includes(',') ? word : `${word},`
              })
              .join('')

            addCard(userId, formattedValue, (e) => {
              setError(e.details)
            })
          }}
        />
      </IFModal>
      <Popover
        id="mouse-over-popover"
        className={Styles.Popover}
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        disableRestoreFocus
      >
        <Box
          className={Styles.ParentBox}
          sx={{
            p: 2,
          }}
        >
          <Box className={Styles.ChildBox}>
            <IFText style={{ color: Colors.DetailPanel }}>
              <strong>{t('ChargingTokenPage.HuidTitle')}</strong>
            </IFText>
            <IFText
              className={Styles.PopoverText}
              style={{
                color: Colors.text,
              }}
            >
              {popoverContent.huid}
            </IFText>
          </Box>

          <Box className={Styles.SecondChildBox}>
            <IFText style={{ color: Colors.DetailPanel }}>
              <strong>{t('ChargingTokenPage.ContractIdTitle')}</strong>
            </IFText>
            <IFText
              className={Styles.PopoverText}
              style={{
                color: Colors.text,
              }}
            >
              {popoverContent.contractId}
            </IFText>
          </Box>
        </Box>
      </Popover>
    </>
  )
}

function mapDispatchToProps(dispatch) {
  return {
    deleteCurrentUserPaymentCard: (userId, paymentCardId, index) => {
      dispatch(
        UserActions.deleteCurrentUserPaymentCard(userId, paymentCardId, index),
      )
    },
    addCard: (id, serialNumbers, onError) =>
      dispatch(UserActions.addCurrentUserNewCard(id, serialNumbers, onError)),
    setCurrentUserChargingTokenActive: (userId, chargingTokenId, index) =>
      dispatch(
        UserActions.setCurrentUserChargingTokenActive(
          userId,
          chargingTokenId,
          index,
        ),
      ),
  }
}

const mapStateToProps = (state) => ({
  currentUserChargingTokens: UserSelectors.getCurrentUserChargingTokens(state),
  editingAdminRole: AuthSelectors.getEditingAdminRole(state),
  userDetailsRequestState: UserSelectors.getUserDetailsRequestState(state),
  fetchUserListRequestState: UserSelectors.getFetchUserListRequestState(state),
  addCurrentCardRequestState:
    UserSelectors.getAddCurrentCardRequestState(state),
  admin: AuthSelectors.getUser(state),
  selectedUserIndex: UserSelectors.getSelectedUserIndex(state),
})

UserChargingTokenList.propTypes = {
  currentUserChargingTokens: PropTypes.arrayOf(PropTypes.object),
  setCurrentUserChargingTokenActive: PropTypes.func,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(UserChargingTokenList)
